import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

export default function C_E_RANGE({
  tempValue,
  onChange, // Added to support standard popup data flow
  onBlur, // Added to support standard popup data flow
  onDirectSave, // Existing logic for direct cell editing
  onKeyDown,
  min = 1,
  max = 100,
  step = 1,
  inPopup = false, // Added popup mode flag
}) {
  const properties_state = useSelector((state) => state.properties);
  const mode_map = properties_state.mode_map; //editor || viewer

  const [localValue, setLocalValue] = useState(
    tempValue !== undefined && tempValue !== "" ? Number(tempValue) : min,
  );
  const [isFocused, setIsFocused] = useState(false); // Added for helper text

  // Safety action state: locked by default in popup mode
  const [isEditingEnabled, setIsEditingEnabled] = useState(false);

  const inputRef = useRef(null);

  // Auto-focus logic: avoid auto-focusing in popup mode unless explicitly desired,
  // but keep it for grid cells.
  useEffect(() => {
    if (!inPopup && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inPopup]);

  // Sync state if tempValue changes externally (important for standard React form flow in popups)
  useEffect(() => {
    if (inPopup && tempValue !== undefined) {
      setLocalValue(Number(tempValue) || min);
    }
  }, [tempValue, inPopup, min]);

  const range = max - min || 1;
  const percentage = Math.max(
    0,
    Math.min(100, ((localValue - min) / range) * 100),
  );

  const thumbWidth = 20;
  const thumbOffset = thumbWidth / 2 - (percentage / 100) * thumbWidth;

  // Determines if the component should accept input
  const isActive = mode_map === "editor" && (!inPopup || isEditingEnabled);

  const handleDrag = (e) => {
    if (!isActive) return;
    setLocalValue(Number(e.target.value));
    if (onChange) onChange(e); // Propagate upwards for the popup state
  };

  const handleRelease = (e) => {
    e.stopPropagation();
    if (inPopup) {
      // In a popup, releasing the slider acts as a blur/save trigger
      setIsEditingEnabled(false); // Lock it again after saving
      if (onBlur) onBlur(e);
    } else {
      // Directly push the exact dragged value up and close the cell
      if (onDirectSave) onDirectSave(localValue);
    }
  };

  const handleBlurEvent = (e) => {
    setIsFocused(false);
    setIsEditingEnabled(false); // Failsafe lock when clicking away
    if (inPopup && onBlur) onBlur(e);
    if (!inPopup && onDirectSave) handleRelease(e); // Failsafe if they click away in grid mode
  };

  const handleKeyDownLocal = (e) => {
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
      e.stopPropagation();
    } else if (e.key === "Enter" || e.key === "Escape") {
      setIsEditingEnabled(false); // Lock on enter/escape
      if (inPopup) {
        e.target.blur(); // Force blur to hide helper text and trigger onBlur save
        if (onKeyDown) onKeyDown(e);
      } else {
        if (onDirectSave) onDirectSave(localValue);
        if (onKeyDown) onKeyDown(e);
      }
    }
  };

  const enableEditing = (e) => {
    e.stopPropagation();

    // Guard clause: If not in editor mode, do nothing
    if (mode_map !== "editor") return;

    if (inPopup && !isEditingEnabled) {
      setIsEditingEnabled(true);
      setIsFocused(true);
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 0);
    }
  };

  const rangeSliderContent = (
    <div
      style={{
        width: "100%",
        height: inPopup ? "40px" : "100%", // Fixed height for popup, 100% for grid cell
        backgroundColor: "#dedede",
        padding: "0 8px",
        borderRadius: inPopup ? "4px" : "0", // Rounded corners look better in popup
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        cursor: isActive ? "default" : "pointer", // Show pointer when locked to encourage clicking
        opacity: isActive ? 1 : 0.85, // Slight dim when locked
      }}
      onClick={enableEditing} // Click wrapper to enable editing
    >
      <span style={{ fontSize: "11px", color: "#555", userSelect: "none" }}>
        {min}
      </span>

      <div
        style={{
          position: "relative",
          flex: 1,
          height: "24px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Background Track */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: "4px",
            backgroundColor: "#b8b8b8",
            borderRadius: "2px",
            pointerEvents: "none",
          }}
        />

        {/* Fill Track */}
        <div
          style={{
            position: "absolute",
            left: 0,
            width: `calc(${percentage}% + ${thumbOffset}px)`,
            height: "4px",
            backgroundColor: isActive ? "#000" : "#888", // Greyed out if locked
            borderRadius: "2px",
            pointerEvents: "none",
            transition: "background-color 0.2s ease",
          }}
        />

        {/* Custom Thumb */}
        <div
          style={{
            position: "absolute",
            left: `calc(${percentage}% + ${thumbOffset}px)`,
            transform: "translateX(-50%)",
            width: `${thumbWidth}px`,
            height: `${thumbWidth}px`,
            borderRadius: "50%",
            backgroundColor: isActive ? "#000" : "#888", // Greyed out if locked
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            fontWeight: "bold",
            pointerEvents: "none",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            zIndex: 1,
            transition: "background-color 0.2s ease",
          }}
        >
          {localValue}
        </div>

        {/* Invisible Native Input */}
        <input
          ref={inputRef}
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          onChange={handleDrag}
          onMouseUp={handleRelease}
          onTouchEnd={handleRelease}
          onFocus={() => {
            if (isActive) setIsFocused(true);
          }}
          onBlur={handleBlurEvent}
          onKeyDown={handleKeyDownLocal}
          className={inPopup ? "input" : "input_table"}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            margin: 0,
            padding: 0,
            opacity: 0,
            cursor: isActive ? "pointer" : "default",
            zIndex: 2,
            pointerEvents: isActive ? "auto" : "none", // Prevent native drag until unlocked
          }}
        />
      </div>

      <span style={{ fontSize: "11px", color: "#555", userSelect: "none" }}>
        {max}
      </span>
    </div>
  );

  // 1. POPUP MODE
  if (inPopup) {
    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {rangeSliderContent}
        {/* Dynamic Helper Text based on active state */}
        <p
          className="text_small"
          style={{
            margin: "4px 0 0 0",
            color: "#666",
            fontSize: "12px",
            minHeight: "18px",
          }}
        >
          {mode_map !== "editor" ? (
            <span>View Only Mode</span>
          ) : !isEditingEnabled ? (
            <span>Click slider to edit</span>
          ) : (
            isFocused && (
              <span>
                <strong>Enter</strong> or release to save
              </span>
            )
          )}
        </p>
      </div>
    );
  }

  // 2. GRID CELL MODE
  return rangeSliderContent;
}
